import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import ListPage from '../../../sharedModules/listPage/ListPage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import StatusCell from '../../../sharedModules/layout/StatusCell';

import { SelectOption } from '../../../sharedModules/form/model';

import axios from '../../../sharedModules/utils/axios.utils';

const WaterAuthoritySubmissionsPage: React.FunctionComponent = () => {
  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchFilterOptions = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get('/backflows/backflow_job_groups/new');
      setCustomerOptions(response?.data?.client_options || []);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Failed to load filter options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const waterAuthorityFilters = [
    {
      label: 'Customer',
      type: 'select',
      choices: customerOptions,
      param: 'company_name_eq',
    },
    {
      label: 'Status',
      type: 'select',
      choices: [
        { label: 'Approved', value: 'approved' },
        { label: 'Submitted', value: 'submitted' },
        { label: 'Overdue', value: 'overdue' },
        { label: 'Closed No Log', value: 'closed_no_log' },
      ],
      param: 'status_eq',
    },

    {
      label: 'Scheduled',
      type: 'datePicker',
      param: 'backflow_dates_scheduled_date',
      width: 5,
    },
  ];

  const waterAuthorityColumns = [
    {
      name: 'Scheduled',
      selector: 'schedule_dates',
      sortable: true,
      cell: (row: any) => {
        const scheduleDates = row.schedule_dates || '';
        if (scheduleDates.includes(' - ')) {
          const [startDate, endDate] = scheduleDates.split(' - ');
          return (
            <div style={{ lineHeight: '1.4' }}>
              <div>{startDate}</div>
              <div>{endDate}</div>
            </div>
          );
        }
        return scheduleDates;
      },
    },
    {
      name: 'Order #',
      selector: 'order_no',
      sortable: true,
      grow: 0.75,
    },
    {
      name: 'Customer',
      selector: 'company_name',
      sortable: true,
    },
    {
      name: 'Water Authority',
      selector: 'water_authority_name',
      sortable: true,
    },
    {
      name: 'Logging Tech',
      selector: 'display_emp_id',
      sortable: true,
    },
    {
      name: 'Authority-Provided Values Complete',
      selector: 'authority_provided_fields',
      cell: (row: any) => <StatusCell status={row.authority_provided_values_complete_for_all_items} />,
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      cell: (row: any) => <StatusCell status={row.status} />,
      grow: 0.75,
    },
  ];

  const defaultFilterSetting = { status_eq: 'approved' };
  const clearFilterSetting = {
    status_eq: 'approved',
    company_name_eq: '',
    backflow_dates_scheduled_date_gteq: '',
    backflow_dates_scheduled_date_lteq: '',
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={loading} message="Loading Filter Options">
          <ErrorMessage message={errorMessage} />
        </LoadingWrapper>
      </Grid>
      <Grid item xs={12}>
        <ListPage
          clickableRows
          clearedFilterSetting={clearFilterSetting}
          columns={waterAuthorityColumns}
          controller="backflows/backflow_job_groups"
          defaultFilters={defaultFilterSetting}
          filterDefinitions={waterAuthorityFilters}
          includeFilterBar
          queryPlaceholder="Order No"
          title="Backflow Testing"
          subtitle="Water Authority Submissions"
          additionalParams={{}}
        />
      </Grid>
    </Grid>
  );
};

export default WaterAuthoritySubmissionsPage;
